import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { CheckCircleIcon, MapPinIcon, NoSymbolIcon } from '@heroicons/react/16/solid';
import { Dialog } from '../../common/Catalyst/dialog';
import { Button } from '../../common/Catalyst/button';
import { HoaReturn, VendorReturn } from '../../clients/rust-api-client';
import { vendorService } from '../../clients/ApiClients';
import { apiErrorMessage } from '../../utils/apiErrors';
import { ErrorAlert } from '../../common/Custom/ErrorAlert';
import { PropertyPicker } from '../../violation/linear/PropertyPicker';
import { CreateScope } from '../../shell/createScope';
import { ScopeChips, useScopeState } from '../../shell/ScopeChips';
import usStates from '../../utils/addressUtils';
import {
  EMPTY_VENDOR_DRAFT,
  NAME_MAX,
  VENDOR_STATUSES,
  VendorDraft,
  VendorStatus,
  buildVendorCreate,
  missingForVendor,
  vendorFieldErrors,
} from './vendorMeta';

/**
 * Create a vendor the Linear way (#665, after #632): a large name, a
 * free-form description, and a row of property chips — the association
 * and the status. Contact details are free text, so they stay labelled
 * inputs below the chips; the address and notes are optional and wait
 * behind "Add address" and "Add notes".
 *
 * A vendor belongs to exactly one association. An HOA's own vendor list
 * passes that association, locked.
 */
interface Props {
  open: boolean;
  hoas: HoaReturn[];
  hoasLoading?: boolean;
  scope?: CreateScope;
  /** The page fixed the association; its chip is not changeable. */
  lockScope?: boolean;
  onClose: () => void;
  onCreated: (vendor: VendorReturn) => void;
}

const StatusGlyph: React.FC<{ status: VendorStatus }> = ({ status }) =>
  status === 'active' ? (
    <CheckCircleIcon className="h-3.5 w-3.5 text-success" aria-hidden />
  ) : (
    <NoSymbolIcon className="h-3.5 w-3.5 text-fg-muted" aria-hidden />
  );

export const CreateVendorModal: React.FC<Props> = ({
  open,
  hoas,
  hoasLoading,
  scope = {},
  lockScope,
  onClose,
  onCreated,
}) => {
  const { hoaId, setHoaId } = useScopeState(open, scope, hoas);
  const [draft, setDraft] = useState<VendorDraft>(EMPTY_VENDOR_DRAFT);
  const [touched, setTouched] = useState<Set<keyof VendorDraft>>(new Set());
  const [showAddress, setShowAddress] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDraft(EMPTY_VENDOR_DRAFT);
    setTouched(new Set());
    setShowAddress(false);
    setShowNotes(false);
    setError(null);
  }, [open]);

  const set =
    <K extends keyof VendorDraft>(key: K) =>
    (value: VendorDraft[K]) =>
      setDraft((d) => ({ ...d, [key]: value }));
  const touch = (key: keyof VendorDraft) => () =>
    setTouched((t) => new Set(t).add(key));

  const full = { ...draft, hoaId };
  const missing = missingForVendor(full);
  const fieldErrors = vendorFieldErrors(full);
  const request = buildVendorCreate(full);
  const canSubmit = !!request && !saving;
  const state = usStates.find((s) => s.value === draft.state);
  const statusLabel =
    VENDOR_STATUSES.find((s) => s.value === draft.status)?.label ?? draft.status;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!request || saving) return;
    setSaving(true);
    setError(null);
    try {
      const response = await vendorService.createVendor({
        vendorCreate: request,
      });
      onCreated(response.data);
      onClose();
    } catch (err) {
      setError(
        apiErrorMessage(err, 'The vendor was not created. Please try again.')
      );
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    'w-full rounded-md border bg-transparent px-2 py-1 text-[13px] text-fg placeholder:text-fg-faint focus:outline-none focus:ring-2 focus:ring-accent';
  const field = (
    key: keyof VendorDraft,
    label: string,
    props: React.InputHTMLAttributes<HTMLInputElement> = {},
    className?: string
  ) => {
    const problem = touched.has(key) ? fieldErrors[key] : undefined;
    return (
      <label className={clsx('flex flex-col gap-1 text-[12px] text-fg-muted', className)}>
        {label}
        <input
          value={(draft[key] as string | null) ?? ''}
          onChange={(e) => set(key)(e.target.value as never)}
          onBlur={touch(key)}
          aria-invalid={!!problem}
          className={clsx(inputClass, problem ? 'border-danger' : 'border-line')}
          {...props}
        />
        {problem && <span className="text-danger">{problem}</span>}
      </label>
    );
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} size="2xl">
      <form onSubmit={submit} aria-label="New vendor">
        <p className="text-[12px] font-medium text-fg-muted">New vendor</p>

        <input
          autoFocus
          value={draft.name}
          onChange={(e) => set('name')(e.target.value)}
          maxLength={NAME_MAX}
          placeholder="Vendor name"
          aria-label="Name"
          className="mt-2 w-full border-0 bg-transparent p-0 text-xl font-semibold text-fg placeholder:text-fg-faint focus:outline-none focus:ring-0"
        />
        <textarea
          value={draft.description}
          onChange={(e) => set('description')(e.target.value)}
          rows={2}
          placeholder="What they do for this association."
          aria-label="Description"
          className="mt-3 w-full resize-none border-0 bg-transparent p-0 text-[15px]/6 text-fg-secondary placeholder:text-fg-faint focus:outline-none focus:ring-0"
        />

        <div className="mt-2 flex flex-wrap items-center gap-1 border-t border-line/60 pt-3">
          <ScopeChips
            hoas={hoas}
            hoasLoading={hoasLoading}
            hoaId={hoaId}
            onHoaChange={setHoaId}
            lockHoa={lockScope && !!scope.hoaId}
          />
          <PropertyPicker
            options={VENDOR_STATUSES.map((s) => ({
              value: s.value,
              label: s.label,
              icon: <StatusGlyph status={s.value} />,
            }))}
            value={draft.status}
            onChange={(v) => set('status')(v as VendorStatus)}
            ariaLabel={`Status: ${statusLabel}`}
            trigger={
              <>
                <StatusGlyph status={draft.status} />
                {statusLabel}
              </>
            }
          />
        </div>

        <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {field('serviceTypes', 'Services', {
            placeholder: 'Landscaping, irrigation',
          })}
          {field('contactName', 'Contact name', { autoComplete: 'off' })}
          {field('phone', 'Phone', { type: 'tel' })}
          {field('email', 'Email', { type: 'email' })}
          {field('website', 'Website', { type: 'url', placeholder: 'https://' }, 'sm:col-span-2')}
        </div>

        {(!showAddress || !showNotes) && (
          <div className="mt-3 flex flex-wrap gap-4">
            {!showAddress && (
              <button
                type="button"
                onClick={() => setShowAddress(true)}
                className="text-[13px] text-accent-fg hover:underline"
              >
                Add address
              </button>
            )}
            {!showNotes && (
              <button
                type="button"
                onClick={() => setShowNotes(true)}
                className="text-[13px] text-accent-fg hover:underline"
              >
                Add notes
              </button>
            )}
          </div>
        )}

        {showAddress && (
          <fieldset className="mt-4">
            <legend className="text-[12px] font-medium text-fg-secondary">Address</legend>
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-6">
              {field('address', 'Street', { autoFocus: true }, 'sm:col-span-6')}
              {field('city', 'City', {}, 'sm:col-span-3')}
              <div className="flex flex-col gap-1 text-[12px] text-fg-muted sm:col-span-1">
                State
                <PropertyPicker
                  options={usStates.map((s) => ({ value: s.value, label: s.label }))}
                  value={draft.state}
                  onChange={set('state')}
                  placeholder="State…"
                  ariaLabel={state ? `State: ${state.label}` : 'Choose state'}
                  className="border border-line"
                  trigger={
                    <>
                      <MapPinIcon className="h-3.5 w-3.5 text-fg-muted" aria-hidden />
                      {state ? state.value : <span className="text-fg-muted">—</span>}
                    </>
                  }
                />
              </div>
              {field('zip', 'ZIP', { inputMode: 'numeric' }, 'sm:col-span-2')}
            </div>
          </fieldset>
        )}

        {showNotes && (
          <textarea
            autoFocus={!showAddress}
            value={draft.notes}
            onChange={(e) => set('notes')(e.target.value)}
            rows={2}
            placeholder="Internal notes — rates, who to call, how they invoice."
            aria-label="Notes"
            className={clsx(inputClass, 'mt-3 resize-none border-line')}
          />
        )}

        <ErrorAlert className="mt-3">{error}</ErrorAlert>

        <div className="mt-5 flex flex-wrap items-center justify-end gap-2 border-t border-line/60 pt-4">
          {missing.length > 0 && (draft.name || draft.description) && (
            <p className="mr-auto text-[12px] text-fg-muted">
              Still needs {missing.join(', ')}.
            </p>
          )}
          <Button plain type="button" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            color="sky"
            type="submit"
            disabled={!canSubmit}
            title={missing.length ? `Add ${missing.join(', ')}` : undefined}
          >
            {saving ? 'Creating…' : 'Create vendor'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
};
